/**
 * Custom hooks for checking employer-specific capabilities
 * Used to conditionally render employer platform features
 *
 * Employer capabilities are separate from organization (CBO) capabilities
 * and are checked against the employer entity, not the organization.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '@/services/apiClient';

// ============================================================================
// Types
// ============================================================================

interface EmployerCapabilityResponse {
  capability_name: string;
  enabled: boolean;
  config: Record<string, any>;
}

interface EmployerCapabilityBundle {
  name: string;
  capabilities: string[];
}

// ============================================================================
// Employer Capability Definitions
// ============================================================================

/**
 * All employer capabilities organized by tier
 */
export const EMPLOYER_CAPABILITIES = {
  // Core platform capability (required for all others)
  employer_platform: {
    label: 'Employer Navigation Platform',
    description: 'Core employer platform access',
    tier: 'basic',
    required: true,
  },

  // Basic tier
  employer_employee_portal: {
    label: 'Employee Self-Service Portal',
    description: 'Employee portal with program discovery and enrollment',
    tier: 'basic',
    requires: 'employer_platform',
  },

  // Standard tier
  employer_navigation_sessions: {
    label: 'Navigation Sessions',
    description: 'AI and human navigation sessions',
    tier: 'standard',
    requires: 'employer_platform',
  },
  employer_analytics: {
    label: 'Employer Analytics',
    description: 'Analytics dashboard and reporting',
    tier: 'standard',
    requires: 'employer_platform',
  },

  // Premium tier
  employer_recommendations: {
    label: 'AI Recommendations',
    description: 'AI-powered program recommendations',
    tier: 'premium',
    requires: 'employer_platform',
  },
  employer_nudge_engine: {
    label: 'Nudge Engine',
    description: 'Automated engagement nudges',
    tier: 'premium',
    requires: 'employer_platform',
  },
  employer_hris_integration: {
    label: 'HRIS Integration',
    description: 'Workday, ADP, UKG, BambooHR sync',
    tier: 'premium',
    requires: 'employer_platform',
  },

  // Enterprise tier
  employer_leave_disability: {
    label: 'Leave & Disability',
    description: 'FMLA, ADA, return-to-work management',
    tier: 'enterprise',
    requires: 'employer_platform',
  },
  employer_workspace_messaging: {
    label: 'Workspace Messaging',
    description: 'Slack and Teams integration',
    tier: 'enterprise',
    requires: 'employer_platform',
  },
  employer_protected_mode: {
    label: 'Protected Mode',
    description: 'Anonymous browsing for sensitive topics',
    tier: 'enterprise',
    requires: 'employer_platform',
  },
} as const;

export type EmployerCapabilityName = keyof typeof EMPLOYER_CAPABILITIES;

/**
 * Capability bundles by tier
 */
export const EMPLOYER_CAPABILITY_BUNDLES: Record<string, EmployerCapabilityName[]> = {
  basic: ['employer_platform', 'employer_employee_portal'],
  standard: ['employer_platform', 'employer_employee_portal', 'employer_navigation_sessions', 'employer_analytics'],
  premium: [
    'employer_platform', 'employer_employee_portal', 'employer_navigation_sessions',
    'employer_analytics', 'employer_recommendations', 'employer_nudge_engine', 'employer_hris_integration',
  ],
  enterprise: [
    'employer_platform', 'employer_employee_portal', 'employer_navigation_sessions',
    'employer_analytics', 'employer_recommendations', 'employer_nudge_engine', 'employer_hris_integration',
    'employer_leave_disability', 'employer_workspace_messaging', 'employer_protected_mode',
  ],
};

// ============================================================================
// Single Capability Hook
// ============================================================================

/**
 * Hook for checking if a specific employer capability is enabled
 *
 * @param employerId - The employer ID to check capabilities for
 * @param capabilityName - The capability name to check
 * @returns { enabled, config, loading, error }
 */
export const useEmployerCapability = (
  employerId: number | null,
  capabilityName: EmployerCapabilityName | string
) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [config, setConfig] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employerId) {
      setLoading(false);
      setEnabled(false);
      return;
    }

    const checkCapability = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, get the employer to find the organization ID
        const employerResponse = await apiClient.get(`/api/employers/${employerId}`);
        const employer = employerResponse.data;
        const orgId = employer.cbo_id || employerId;

        // Then check the capability against the organization
        const response = await apiClient.get(
          `/api/organizations/${orgId}/capabilities/${capabilityName}`
        );

        const data: EmployerCapabilityResponse = response.data;
        setEnabled(data.enabled);
        setConfig(data.config || {});
      } catch (err: any) {
        console.error(`Failed to check employer capability ${capabilityName}:`, err);
        setError(err.response?.data?.detail || err.message || 'Failed to check capability');
        setEnabled(false);
        setConfig({});
      } finally {
        setLoading(false);
      }
    };

    checkCapability();
  }, [employerId, capabilityName]);

  return { enabled, config, loading, error };
};

// ============================================================================
// Multiple Capabilities Hook
// ============================================================================

/**
 * Hook for checking multiple employer capabilities at once
 */
export const useEmployerCapabilities = (
  employerId: number | null,
  capabilityNames: (EmployerCapabilityName | string)[]
) => {
  const [capabilities, setCapabilities] = useState<Record<string, boolean>>({});
  const [configs, setConfigs] = useState<Record<string, Record<string, any>>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<number | null>(null);

  useEffect(() => {
    if (!employerId || capabilityNames.length === 0) {
      setLoading(false);
      setCapabilities({});
      return;
    }

    const checkCapabilities = async () => {
      try {
        setLoading(true);
        setError(null);

        // First, get the employer to find the organization ID
        const employerResponse = await apiClient.get(`/api/employers/${employerId}`);
        const employer = employerResponse.data;
        const organizationId = employer.cbo_id || employerId;
        setOrgId(organizationId);

        // Fetch all capabilities in parallel
        const responses = await Promise.all(
          capabilityNames.map(name =>
            apiClient.get(
              `/api/organizations/${organizationId}/capabilities/${name}`
            ).catch(err => {
              console.warn(`Failed to check employer capability ${name}:`, err);
              return { data: { capability_name: name, enabled: false, config: {} } };
            })
          )
        );

        const capabilitiesMap: Record<string, boolean> = {};
        const configsMap: Record<string, Record<string, any>> = {};

        responses.forEach(response => {
          const item: EmployerCapabilityResponse = response.data;
          capabilitiesMap[item.capability_name] = item.enabled;
          configsMap[item.capability_name] = item.config || {};
        });

        setCapabilities(capabilitiesMap);
        setConfigs(configsMap);
      } catch (err: any) {
        console.error('Failed to check employer capabilities:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to check capabilities');
        const disabledMap: Record<string, boolean> = {};
        capabilityNames.forEach(name => {
          disabledMap[name] = false;
        });
        setCapabilities(disabledMap);
        setConfigs({});
      } finally {
        setLoading(false);
      }
    };

    checkCapabilities();
  }, [employerId, JSON.stringify(capabilityNames)]);

  const hasCapability = useCallback(
    (capabilityName: string): boolean => {
      return capabilities[capabilityName] || false;
    },
    [capabilities]
  );

  const getCapabilityConfig = useCallback(
    (capabilityName: string): Record<string, any> => {
      return configs[capabilityName] || {};
    },
    [configs]
  );

  return {
    capabilities,
    configs,
    hasCapability,
    getCapabilityConfig,
    loading,
    error,
    orgId,
  };
};

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook for checking if ALL specified employer capabilities are enabled
 */
export const useRequireEmployerCapabilities = (
  employerId: number | null,
  requiredCapabilities: (EmployerCapabilityName | string)[]
) => {
  const { capabilities, loading, error } = useEmployerCapabilities(employerId, requiredCapabilities);

  const allEnabled = useMemo(() => {
    if (loading) return false;
    return requiredCapabilities.every(cap => capabilities[cap]);
  }, [capabilities, loading, requiredCapabilities]);

  const missingCapabilities = useMemo(() => {
    if (loading) return [];
    return requiredCapabilities.filter(cap => !capabilities[cap]);
  }, [capabilities, loading, requiredCapabilities]);

  return { allEnabled, missingCapabilities, loading, error };
};

/**
 * Hook for checking if ANY of the specified employer capabilities are enabled
 */
export const useHasAnyEmployerCapability = (
  employerId: number | null,
  capabilityNames: (EmployerCapabilityName | string)[]
) => {
  const { capabilities, loading, error } = useEmployerCapabilities(employerId, capabilityNames);

  const hasAny = useMemo(() => {
    if (loading) return false;
    return capabilityNames.some(cap => capabilities[cap]);
  }, [capabilities, loading, capabilityNames]);

  const enabledCapabilities = useMemo(() => {
    if (loading) return [];
    return capabilityNames.filter(cap => capabilities[cap]);
  }, [capabilities, loading, capabilityNames]);

  return { hasAny, enabledCapabilities, loading, error };
};

/**
 * Hook version of EmployerCapabilityGate for use in component logic
 */
export const useEmployerCapabilityGate = (
  employerId: number | null,
  capability: EmployerCapabilityName | string
) => {
  const { enabled, config, loading, error } = useEmployerCapability(employerId, capability);

  return {
    isEnabled: enabled,
    isLoading: loading,
    error,
    config,
    capabilityInfo: EMPLOYER_CAPABILITIES[capability as EmployerCapabilityName],
  };
};

/**
 * Hook for getting the current employer's capability tier
 */
export const useEmployerCapabilityTier = (employerId: number | null) => {
  const allCapabilities = Object.keys(EMPLOYER_CAPABILITIES) as EmployerCapabilityName[];
  const { capabilities, loading, error } = useEmployerCapabilities(employerId, allCapabilities);

  const tier = useMemo(() => {
    if (loading) return null;

    const enabledCaps = Object.entries(capabilities)
      .filter(([_, enabled]) => enabled)
      .map(([name, _]) => name);

    // Check tiers from highest to lowest
    const tiers = ['enterprise', 'premium', 'standard', 'basic'] as const;

    for (const tierName of tiers) {
      const tierCaps = EMPLOYER_CAPABILITY_BUNDLES[tierName];
      if (tierCaps.every(cap => enabledCaps.includes(cap))) {
        return tierName;
      }
    }

    return enabledCaps.includes('employer_platform') ? 'basic' : 'none';
  }, [capabilities, loading]);

  return { tier, capabilities, loading, error };
};

// ============================================================================
// Context Hook (for use with EmployerContext)
// ============================================================================

/**
 * Hook that uses the current employer from context/localStorage
 */
export const useCurrentEmployerCapability = (capability: EmployerCapabilityName | string) => {
  const [employerId, setEmployerId] = useState<number | null>(null);

  useEffect(() => {
    // Get employer ID from localStorage (set by employer context)
    const storedEmployerId = localStorage.getItem('active_employer_id');
    if (storedEmployerId) {
      setEmployerId(parseInt(storedEmployerId, 10));
    }
  }, []);

  return useEmployerCapability(employerId, capability);
};

export default useEmployerCapabilities;
